import { Fetcher } from '@/api/fetcher'
import { API_URL } from '@/constants'
import { ApiError } from '@/errors/apiError'
import { Chatroom, MessageSortKeys, MessageTypes } from '@/types/api/chat-room'
import { PaginationResponse } from '@/types/api/pagination-response'
import { ApiHandlerResult } from '..'
import { BaseApi } from '../base'

interface CreateChatroomPayload {
  content?: string
  chat_type: MessageTypes
  participant_id: number
  recycle_item_id?: number
  participant_supplier_id?: number
  creator_email?: string
  creator_full_name?: string
  withEmail?: boolean
}

interface GetChatRoomsOptionsType {
  page?: number
  pageSize?: number
  messageType?: MessageTypes
  sortKey?: MessageSortKeys
  keyword?: string
  businessCategoryId?: string
  recycleId?: number
}

const DEFAULT_CHATROOMS_PAGE_SIZE = 9

export class ChatRoomApi extends BaseApi {
  constructor(fetcher: Fetcher) {
    super(fetcher)
  }

  async search(
    params?: GetChatRoomsOptionsType,
    options?: RequestInit
  ): ApiHandlerResult<PaginationResponse<Chatroom>> {
    try {
      const res = await this.fetcher.get(API_URL.CHATROOMS.ROOT, {
        params: {
          ...params,
          total: params?.pageSize,
        },
        ...options,
      })

      if (res.status !== 200) {
        const errBody = await res.json()
        return [new ApiError(res.status, errBody), null]
      }

      const body = await res.json()

      // Until the endpoint is rewritten, the pagination is calculated here.
      const paginatedBody = {
        results: body.data as Chatroom[],
        pagination: {
          total: body.total as number,
          pageSize: (params?.pageSize as number) || DEFAULT_CHATROOMS_PAGE_SIZE,
          page: (params?.page as number) || 1,
        },
      }

      return [null, paginatedBody]
    } catch (err: any) {
      return [err, null]
    }
  }

  async create(
    body: CreateChatroomPayload,
    options?: RequestInit
  ): ApiHandlerResult<{ chat_id: number; chatroom_id: number }> {
    try {
      const res = await this.fetcher.post(API_URL.CHATROOMS.ROOT, {
        body: JSON.stringify(body),
        ...options,
      })

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }

  async sendMessage(
    chatroomId: number,
    body: { content: string },
    options?: RequestInit
  ): ApiHandlerResult<{ chat_id: number; message: string }> {
    try {
      const res = await this.fetcher.post(
        `/chatroom/${chatroomId}/chat`,
        {
          body: JSON.stringify(body),
          ...options,
        }
      )

      return await this.parseBodyOrThrow(res)
    } catch (err: any) {
      return [err, null]
    }
  }
}
